// localStorage 按身份命名空间化的访问层。
//
// 背景：同一浏览器下嵌入/打开多个渠道时（不同的 configId），
// 全局 key 会互相覆盖，导致前一个渠道的会话被后一个渠道挤掉。这里按身份维度拆开：
//
// 访客命名空间：`__ABOT_ACCESS_TOKEN__:<channelId>` —— 一个访客可能同一浏览器
//   接入多个渠道，需要按 channelId 隔离
// 客服命名空间：`__ABOT_AGENT_TOKEN__:<memberId>` —— 客服身份的隔离维度是自己
//   （memberId），跨会话/跨渠道复用同一份客服 token，无需按 channelId 拆
//
// 角色与身份状态（模块级）：
// - currentRole：'customer' | 'agent'，由入口 index.js 在 Vue 挂载前明确写入
// - currentMemberId：客服 URL 的 config 参数值（客服的 memberId），仅 agent 有效
// - overrideChannelId：客服模式下真实的 channelId（从 /merchant/chats/userInfo 拉到后写入）
//
// 为什么用模块级而不是 pinia store：chat store 首次 useChatStore() 时会立即读取
// getToken()/getChannelId() 构造 socket URL，若从 store 里读取 role 会循环依赖。
// 模块级状态让入口能在 store 创建之前把身份彻底定下来。

const TOKEN_PREFIX = '__ABOT_ACCESS_TOKEN__'
const AGENT_TOKEN_PREFIX = '__ABOT_AGENT_TOKEN__'

export type WidgetRole = 'customer' | 'agent'

let currentRole: WidgetRole = 'customer'
let currentMemberId = ''
let overrideChannelId = ''

export function setRole(role: WidgetRole): void {
  currentRole = role
}

export function getRole(): WidgetRole {
  return currentRole
}

export function setMemberId(id: string): void {
  currentMemberId = id
}

export function getMemberId(): string {
  return currentMemberId
}

// 客服模式下，真实 channelId 需要在拉到 /merchant/chats/userInfo 后写入，
// 这样 chat store 首次实例化构造 socket URL 时能读到正确的 channelId。
// 未写入时 fallback 到 URL 的 ?config 值（访客模式，config 就是 channelId）。
export function setChannelId(id: string): void {
  overrideChannelId = id
}

// 与 index.js 的 getConfigFromScript 行为保持一致：
// 1) 优先返回 setChannelId 写入的覆盖值（客服模式先调 /userInfo 拿到 channelId 后写入）
// 2) 从 <script src=".../widget.js?config=X"> 解析（生产嵌入路径）
// 3) 兜底从当前页面 URL 的 ?config= 解析（widget.html 独立页 / 开发测试）
//    注意：客服模式下 URL 的 config 是 memberId，不是 channelId；因此客服链路一定要
//    先 setChannelId 后再读，否则会拿到 memberId 当 channelId 用（错误）。
// 4) 都没有则回到 'default'
export function getChannelId(): string {
  if (overrideChannelId) return overrideChannelId
  if (typeof document !== 'undefined') {
    const scripts = document.getElementsByTagName('script')
    for (const script of Array.from(scripts)) {
      if (script.src && (script.src.includes('index.js') || script.src.includes('widget.js'))) {
        try {
          const cfg = new URL(script.src).searchParams.get('config')
          if (cfg) return cfg
        } catch {
          // 忽略，继续兜底
        }
      }
    }
  }
  if (typeof window !== 'undefined') {
    const cfg = new URLSearchParams(window.location.search).get('config')
    if (cfg) return cfg
  }
  return 'default'
}

// HTTP 请求头里 Tenant-Id 的值：客服身份用 memberId，访客身份用 channelId。
// 后端底层权限校验对这个字段的语义是"当前调用者的租户/身份边界"，两种角色的
// 边界定义不同，因此不能一律传 channelId。所有 API 层都应通过本函数取值。
export function getTenantId(): string {
  if (currentRole === 'agent') return currentMemberId
  return getChannelId()
}

function tokenKey(channelId: string): string {
  return `${TOKEN_PREFIX}:${channelId}`
}

function agentTokenKey(memberId: string): string {
  return `${AGENT_TOKEN_PREFIX}:${memberId}`
}

// 按当前身份返回对应 token。
// - 访客：按 channelId 命名空间；
// - 客服：按 memberId 命名空间（memberId 未就位则返回 null，避免拿到访客 token 误用）。
export function getToken(channelId: string = getChannelId()): string | null {
  if (currentRole === 'agent') {
    if (!currentMemberId) return null
    return localStorage.getItem(agentTokenKey(currentMemberId))
  }
  return localStorage.getItem(tokenKey(channelId))
}

export function setToken(token: string, channelId: string = getChannelId()): void {
  localStorage.setItem(tokenKey(channelId), token)
}

export function getAgentToken(memberId: string = currentMemberId): string | null {
  if (!memberId) return null
  return localStorage.getItem(agentTokenKey(memberId))
}

export function setAgentToken(token: string, memberId: string = currentMemberId): void {
  if (!memberId) return
  localStorage.setItem(agentTokenKey(memberId), token)
}
